import { useState, useRef, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import { useNotify } from "../../hooks/use-notify";
import { useSingleton } from "../../hooks/use-singleton";
import {
  API_CONVERSATION_START,
  API_CONVERSATION_END,
  API_CONVERSATION_CHAT
} from "../../utils/api-routes";

// ---- Mic / recording states ----
export const MIC_STATE = {
  IDLE: "idle",               
  RECORDING: "recording",      
  TRANSCRIBING: "transcribing"  
};

export const TOPICS_BY_DIFFICULTY = {
  Beginner: [
    "Introducing Myself",
    "My Daily Routine",
    "Favorite Hobbies",
    "My Family & Friends",
    "At School & Classroom",
    "Favorite Subjects",
    "Weekend Fun",
    "Pets & Animals",
  ],
  Intermediate: [
    "School Science Exhibition",
    "Dream Careers & Goals",
    "Saving the Environment",
    "Favorite Books & Stories",
    "Using Computers & Technology",
    "Healthy Habits & Sports",
    "School Trips & Holidays",
    "Overcoming Exam Fears",
  ],
  Advanced: [
    "Impact of AI in Education",
    "Social Media Pros & Cons",
    "Climate Change & Action",
    "Student Leadership & Teamwork",
    "Space Exploration",
    "Science Ethics & Innovations",
    "Importance of Reading Books",
    "Healthy Mind & Mental Health",
  ],
};

export const TOPICS = TOPICS_BY_DIFFICULTY.Beginner;

export const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

export function getFeedbackSummary(score) {
  if (score >= 90) return { title: "Excellent Progress!", message: `You scored ${score}%. Outstanding accuracy and confidence.` };
  if (score >= 75) return { title: "Excellent Progress!", message: `You scored ${score}%. Keep practicing to improve your accuracy and confidence.` };
  if (score >= 50) return { title: "Good Effort!", message: `You scored ${score}%. A bit more practice will sharpen your grammar.` };
  return { title: "Keep Practicing!", message: `You scored ${score}%. Review the feedback below to improve.` };
}

export function getScoreTier(score) {
  if (score >= 75) return "excellent";
  if (score >= 50) return "fair";
  return "poor";
}

const useHandler = ({ onEndConversation } = {}) => {
  const { post } = useApi();
  const { toggleNotify } = useNotify();
  const { mail, user } = useSingleton();

  const userEmail = mail || user?.data?.email || "";

  const [screen, setScreen] = useState("setup"); 
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [messages, setMessages] = useState([]);
  const [micState, setMicState] = useState(MIC_STATE.IDLE);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [ending, setEnding] = useState(false);
  const [starting, setStarting] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);

  // conversation_id from /start, needed by every /chat and /end call
  const conversationIdRef = useRef(null);

  const recognitionRef = useRef(null);
  const transcriptRef = useRef("");
  const chatBodyRef = useRef(null);

  const canStart = topic.trim().length > 0 && difficulty.length > 0;

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, micState]);

  // ---------- /english/conversation/start ----------
  const startConversation = async () => {
    if (!canStart || !userEmail) return;

    setStarting(true);
    try {
      const res = await post(API_CONVERSATION_START, {
        category: topic,
        difficulty,
        user_mail: userEmail
      });

      if (res?.data) {
        conversationIdRef.current = res.data.conversation_id;
        setMessages([{ role: "ai", text: res.data.ai_message, audioUrl: res.data.audio_url }]);
        setScreen("chat");
      }
    } catch (error) {
      console.error("Error starting conversation:", error);
      toggleNotify({
        type: "error",
        title: "Error",
        description: "Failed to start the conversation. Please try again."
      });
    } finally {
      setStarting(false);
    }
  };

  // idle -> recording -> transcribing -> idle
  const handleMicTap = () => {
    if (micState === MIC_STATE.IDLE) startRecording();
    else if (micState === MIC_STATE.RECORDING) stopRecordingAndSend();
  };

  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition isn't supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.continuous = true;

    transcriptRef.current = "";
    recognition.onresult = (event) => {
      let finalTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
      }
      transcriptRef.current += finalTranscript;
    };
    recognition.onerror = () => setMicState(MIC_STATE.IDLE);

    recognitionRef.current = recognition;
    recognition.start();
    setMicState(MIC_STATE.RECORDING);
  };

  // ---------- /english/conversation/chat ----------
  const stopRecordingAndSend = () => {
    recognitionRef.current?.stop();
    setMicState(MIC_STATE.TRANSCRIBING);

    // let the recognizer flush its final result before reading transcriptRef
    setTimeout(() => {
      const transcript = transcriptRef.current.trim();

      if (!transcript) {
        setMicState(MIC_STATE.IDLE);
        return;
      }

      setMessages((prev) => [...prev, { role: "user", text: transcript, feedback: null }]);

      post(API_CONVERSATION_CHAT, {
        conversation_id: conversationIdRef.current,
        student_message: transcript,
        user_mail: userEmail
      })
        .then((res) => {
          const data = res?.data;
          if (!data) return;

          setMessages((prev) => {
            const updated = [...prev];
            const lastUserIndex = updated.length - 1;
            updated[lastUserIndex] = {
              ...updated[lastUserIndex],
              feedback: {
                score: data.score,
                mistakes: data.grammar_feedback,
                corrected: data.improved_sentence,
                score_heading: data.score_heading
              }
            };
            updated.push({ role: "ai", text: data.ai_response, audioUrl: data.audio_url });
            return updated;
          });
        })
        .catch((err) => {
          console.error("Failed to get AI reply", err);
          toggleNotify({
            type: "error",
            title: "Error",
            description: "Failed to send your message. Please try again."
          });
        })
        .finally(() => {
          setMicState(MIC_STATE.IDLE);
        });
    }, 400);
  };

  // ---------- /english/conversation/end ----------
  const endConversation = () => {
    setEnding(true);

    post(API_CONVERSATION_END, {
      conversation_id: conversationIdRef.current,
      user_mail: userEmail
    })
      .then((res) => {
        const data = res?.data;
        if (!data) return;

        // Build metric cards dynamically from whatever { score, label }
        // keys the API actually returns (grammar, fluency, vocabulary,
        // pronunciation today — could be more/fewer later). No hardcoded
        // list of 4 names; the card set just follows the response.
        const KNOWN_NON_METRIC_KEYS = [
          "message",
          "overall_score",
          "turn_scores",
          "total_turns",
          "status",
          "audio_url"
        ];

        const metrics = Object.entries(data)
          .filter(
            ([key, value]) =>
              !KNOWN_NON_METRIC_KEYS.includes(key) &&
              value &&
              typeof value === "object" &&
              typeof value.score === "number"
          )
          .map(([key, value]) => ({
            label: key.toUpperCase(),
            score: value.score,
            tag: value.label
          }));

        const result = {
          overallScore: data.overall_score,
          metrics,
          feedbackMessage: data.message,
          audioUrl: data.audio_url,
          close_heading: data.closing_heading
        };

        setSummaryResult(result);
        setScreen("summary");
        onEndConversation?.({ ...result, topic, difficulty });
      })
      .catch((error) => {
        console.error("Error ending conversation:", error);
        toggleNotify({
          type: "error",
          title: "Error",
          description: "Failed to end the conversation. Please try again."
        });
      })
      .finally(() => {
        setEnding(false);
      });
  };

  const backToDashboard = () => {
    setScreen("setup");
    setTopic("");
    setDifficulty("");
    setMessages([]);
    setSelectedFeedback(null);
    setSummaryResult(null);
    conversationIdRef.current = null;
  };

  return {
    screen,
    topic,
    setTopic,
    difficulty,
    setDifficulty,
    canStart,
    starting,
    startConversation,
    messages,
    micState,
    selectedFeedback,
    setSelectedFeedback,
    chatBodyRef,
    handleMicTap,
    endConversation,
    ending,
    summaryResult,
    backToDashboard
  };
};

export default useHandler;